import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { api } from '../utils/api';

export interface User {
  id: number;
  email: string;
  fullname: string;
  avatar?: string | null;
  avatar_url?: string | null;
  phone_number?: string;
  gender?: string;
  city?: string;
  country?: string;
  language?: string;
  role: string;
  is_superuser: boolean;
  is_staff: boolean;
  is_active: boolean;
  date_joined?: string;
}

export interface Permission {
  id: number;
  codename: string;
  name: string;
  content_type?: {
    id: number;
    app_label: string;
    model: string;
  };
}

export interface PermissionGroup {
  id: number;
  name: string;
  permissions: Permission[];
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  permissions: string[];
  groups: PermissionGroup[];
  isLoading: boolean;
  
  login: (email: string, password: string, captchaToken: string) => Promise<void>;
  logout: () => void;
  fetchProfile: () => Promise<void>;
  
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  canAccessApp: (appName: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        token: null,
        isAuthenticated: false,
        permissions: [],
        groups: [],
        isLoading: false,
        
        login: async (email, password, captchaToken) => {
          set({ isLoading: true });
          try {
            const response = await api.post<{ token: string; user: User }>(
              '/dashboard/account/login/',
              {
                username: email,
                password,
                captcha_token: captchaToken,
              },
              { requiresAuth: false }
            );
            
            const { token, user } = response ?? {};

            if (!token || !user) {
              throw new Error('پاسخ سرور نامعتبر است: توکن یا اطلاعات کاربر دریافت نشد.');
            }

            localStorage.setItem('token', token);
            set({ 
              token, 
              user: {
                ...user,
                avatar: user.avatar_url || user.avatar
              },
              isAuthenticated: true,
              permissions: (user as any).all_permissions || [],
              groups: (user as any).groups || [],
            });
            
            await get().fetchProfile();
          } catch (error: any) {
            console.error('Login failed:', error);
            throw error;
          } finally {
            set({ isLoading: false });
          }
        },
        
        logout: () => {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            permissions: [],
            groups: [],
          });
          localStorage.removeItem('token');
          
          // Redirect to login page
          window.location.href = '/login';
        },
        
        fetchProfile: async () => {
          try {
            const profile = await api.get<any>('/dashboard/account/profile/');
            
            set({
              user: {
                id: profile.id,
                email: profile.email,
                fullname: profile.fullname,
                avatar: profile.avatar_url || profile.avatar,
                avatar_url: profile.avatar_url,
                phone_number: profile.phone_number,
                gender: profile.gender,
                city: profile.city,
                country: profile.country,
                language: profile.language,
                role: profile.role,
                is_superuser: profile.is_superuser,
                is_staff: profile.is_staff,
                is_active: profile.is_active,
                date_joined: profile.date_joined,
              },
              permissions: profile.all_permissions || [],
              groups: profile.groups || [],
            });
          } catch (error: any) {
            console.error('Failed to fetch profile:', error);
            
            // Only logout if it's a 401 (but api.ts already handles this)
            // For other errors (500, network), keep user logged in
            if (error?.status === 401) {
              // api.ts already cleared token and redirected, no need to logout again
              console.warn('Profile fetch failed: Authentication error (already handled by api.ts)');
              return;
            }
            
            // For non-auth errors, log but keep user authenticated
            console.warn('Profile fetch failed, but user remains authenticated', {
              status: error?.status,
              message: error?.message
            });
            
            // Don't logout for network errors or server errors
            throw error; // Let caller handle (e.g., App.tsx)
          }
        },
        
        hasPermission: (permission) => {
          const { user, permissions } = get();
          
          if (user?.is_superuser) return true;
          
          if (permissions.includes('*')) return true;
          
          if (permissions.includes(permission)) return true;
          
          const [app, action] = permission.split('.');
          if (permissions.includes(`${app}.*`)) return true;
          
          return false;
        },
        
        hasAnyPermission: (perms) => {
          return perms.some(p => get().hasPermission(p));
        },
        
        hasAllPermissions: (perms) => {
          return perms.every(p => get().hasPermission(p));
        },
        
        canAccessApp: (appName) => {
          const { user, permissions } = get();
          
          if (user?.is_superuser) return true;
          
          return permissions.some(p => p.startsWith(`${appName}.`));
        },
      }),
      {
        name: 'auth-store',
        partialize: (state) => ({
          token: state.token,
          user: state.user,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);

export const useAuth = () => {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);
  const fetchProfile = useAuthStore((state) => state.fetchProfile);
  
  return { user, isAuthenticated, isLoading, login, logout, fetchProfile };
};

export const usePermissions = () => {
  const permissions = useAuthStore((state) => state.permissions);
  const groups = useAuthStore((state) => state.groups);
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const hasAnyPermission = useAuthStore((state) => state.hasAnyPermission);
  const hasAllPermissions = useAuthStore((state) => state.hasAllPermissions);
  const canAccessApp = useAuthStore((state) => state.canAccessApp);
  
  return {
    permissions,
    groups,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessApp,
  };
};
